import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, ExternalLink, Instagram } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { CONTACT } from "@/constants/testIds";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

export const ContactPage = () => {
  return (
    <div data-testid={CONTACT.section}>
      <Helmet>
        <title>Contact Us | Modern Minds - Book Appointment in Gomti Nagar, Lucknow</title>
        <meta name="description" content="Contact Modern Minds child psychology centre in Gomti Nagar, Lucknow. Book diagnostic assessments and therapy sessions. Call +91-7290900028 or visit our clinic." />
        <link rel="canonical" href="https://www.modernminds.co.in/contact" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <motion.div variants={fadeInUp} className="gradient-divider mx-auto mb-6" />
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            >
              Schedule Clinical Session
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-base sm:text-lg text-[#E2E8F0] max-w-2xl mx-auto"
            >
              Initiate diagnostic mapping or therapeutic intervention with our clinical team.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Grid */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Contact Details Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="glass-card p-6"
            >
              <h2 className="font-heading text-xl font-bold text-white mb-1">
                MODERN MINDS
              </h2>
              <p className="font-body text-[10px] text-[#C9A962] uppercase tracking-wider mb-6">
                Center for Child Psychology; Behavioural Health & Parenting Consultancy
              </p>

              <div className="space-y-5">
                {/* Address */}
                <div data-testid={CONTACT.address} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#C9A962]/20 rounded flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-white text-sm mb-1">
                      Clinical Address
                    </h3>
                    <p className="font-body text-[#E0E1DD] text-sm leading-relaxed">
                      1/4 A Vineet Khand, Gomti Nagar<br />
                      Lucknow, Uttar Pradesh 226010<br />
                      India
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div data-testid={CONTACT.phone} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#C9A962]/20 rounded flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-white text-sm mb-1">
                      Phone
                    </h3>
                    <a
                      href="tel:+919569170611"
                      className="font-body text-[#E0E1DD] text-sm hover:text-[#C9A962] transition-colors"
                    >
                      +91 9569 170 611
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div data-testid={CONTACT.email} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#C9A962]/20 rounded flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-white text-sm mb-1">
                      Email
                    </h3>
                    <a
                      href="mailto:isha@modernminds.co.in"
                      className="font-body text-[#C9A962] text-sm hover:text-white transition-colors"
                    >
                      isha@modernminds.co.in
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div data-testid={CONTACT.hours} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#C9A962]/20 rounded flex items-center justify-center flex-shrink-0">
                    <Clock size={18} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-white text-sm mb-1">
                      Clinical Hours
                    </h3>
                    <div className="font-body text-[#E0E1DD] text-sm space-y-0.5">
                      <p>Monday – Friday: 9:00 AM – 6:00 PM</p>
                      <p>Saturday: 10:00 AM – 2:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Booking Card & Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="space-y-6"
            >
              {/* Booking CTA */}
              <div className="glass-card-gold p-6">
                <h2 className="font-heading text-xl font-bold text-white mb-3">
                  Schedule Appointment
                </h2>
                <p className="font-body text-sm text-[#E0E1DD] mb-4 leading-relaxed">
                  To view available times and schedule an appointment directly, visit our booking portal.
                </p>
                <a
                  href="https://consult.modernminds.co.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={CONTACT.bookingBtn}
                  className="inline-flex items-center gap-2 btn-gold rounded px-6 py-3 font-body font-semibold text-sm mb-4"
                >
                  consult.modernminds.co.in
                  <ExternalLink size={14} />
                </a>
                {/* Data Privacy Clause */}
                <p className="font-body text-xs text-[#778DA9] leading-relaxed border-t border-white/10 pt-4">
                  Your data is encrypted and managed under the Modern Minds Data Privacy Protocol. We do not share information with third-party advertising or referral networks.
                </p>
              </div>

              {/* Map */}
              <div
                data-testid={CONTACT.map}
                className="glass-card overflow-hidden"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3559.5!2d81.0!3d26.85!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399be37eb0826741%3A0x34d9dd79cda84b82!2sVineet%20Khand%2C%20Gomti%20Nagar%2C%20Lucknow%2C%20Uttar%20Pradesh%20226010!5e0!3m2!1sen!2sin!4v1703234567890!5m2!1sen!2sin"
                  width="100%"
                  height="220"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Modern Minds Location - Gomti Nagar, Lucknow"
                  className="w-full"
                />
              </div>

              {/* QR Code Section - Instagram */}
              <div className="glass-card p-5 flex items-center gap-5">
                <a
                  href="https://www.instagram.com/isharastogipsychology/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-2 bg-white rounded-lg hover:scale-105 transition-transform flex-shrink-0"
                  aria-label="Scan QR code or click to visit Instagram"
                >
                  <QRCodeSVG
                    value="https://www.instagram.com/isharastogipsychology/"
                    size={90}
                    level="M"
                    bgColor="#FFFFFF"
                    fgColor="#0F172A"
                  />
                </a>
                <div>
                  <h3 className="font-body font-semibold text-white text-sm mb-2">Connect on Instagram</h3>
                  <a
                    href="https://www.instagram.com/isharastogipsychology/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#7C9885] hover:text-white transition-colors font-body text-sm font-medium"
                  >
                    <Instagram size={18} />
                    @isharastogipsychology
                  </a>
                  <p className="font-body text-xs text-[#94A3B8] mt-2">
                    Scan QR code or click to follow
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
